// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ai.ts — alpha-beta search over game.ts, used by solo play AND by the balance
 * sim (principle #18). The sim is the reason this file is deterministic: given
 * the same seed and the same position it must return the same action, or a
 * "measurement" is just noise with a confident-looking mean.
 */

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  type Action,
  applyUnchecked,
  type GameState,
  heartroot,
  legalActions,
  other,
  pressureOn,
  type Seat,
} from './game';
import { distance, neighborKeys, parseKey } from './hex';
import { handSize } from './modes';

export type Strength = 1 | 2 | 3;

export interface AiConfig {
  strength: Strength;
  seed: number | string;
  /**
   * Search-policy label. The balance sim uses these as its ARMS, so they must
   * be genuinely different policies and not three spellings of one.
   */
  policy?: Policy;
}

/** Distinct playing styles, so the sim measures a game rather than one bot. */
export type Policy = 'balanced' | 'aggressor' | 'turtle';

const WIN = 100_000;

interface Weights {
  attack: number;
  defend: number;
  mobility: number;
  develop: number;
  material: number;
  proximity: number;
}

const POLICIES: Record<Policy, Weights> = {
  balanced: { attack: 46, defend: 44, mobility: 2, develop: 6, material: 8, proximity: 3 },
  aggressor: { attack: 70, defend: 22, mobility: 1, develop: 5, material: 6, proximity: 6 },
  turtle: { attack: 26, defend: 66, mobility: 3, develop: 8, material: 10, proximity: 1 },
};

const DEPTH: Record<Strength, number> = { 1: 1, 2: 2, 3: 3 };

/** How many candidate moves survive ordering at each node. */
const WIDTH: Record<Strength, number> = { 1: 64, 2: 22, 3: 14 };

export function evaluate(s: GameState, me: Seat, w: Weights): number {
  if (s.over) {
    if (s.winner === me) return WIN - s.ply;
    if (s.winner === null) return 0;
    return -WIN + s.ply;
  }
  const foe = other(me);
  let score = pressureOn(s, foe) * w.attack - pressureOn(s, me) * w.defend;
  score += (handSize(s.hands[foe]) - handSize(s.hands[me])) * w.develop;

  let mine = 0;
  let theirs = 0;
  for (const p of s.cells.values()) (p.owner === me ? (mine += 1) : (theirs += 1));
  score += (mine - theirs) * w.material;

  // Being NEAR the enemy Heartroot is worth something even before you ring it —
  // without this the bot has no gradient to climb and shuffles until the cap.
  const target = heartroot(s, foe);
  if (target) {
    let near = 0;
    for (const [k, p] of s.cells) {
      if (p.owner !== me) continue;
      const d = distance(parseKey(k), target);
      if (d <= 3) near += 4 - d;
    }
    score += near * w.proximity;
  }
  return score;
}

/**
 * Cheap static score used only to ORDER moves, so alpha-beta prunes early and
 * the width cap keeps the good moves. Getting this wrong costs strength, never
 * correctness.
 */
function orderScore(s: GameState, a: Action, me: Seat): number {
  const foe = other(me);
  const target = heartroot(s, foe);
  const mine = heartroot(s, me);
  const to = a.k === 'pass' ? null : a.to;
  if (!to) return -1000;
  let v = 0;
  if (target) v += 30 - distance(to, target) * 8;
  if (mine) {
    // Filling a cell next to your OWN Heartroot is usually a blunder: occupied
    // is occupied, and the ring does not care whose piece it is.
    for (const nk of neighborKeys(mine)) if (nk === `${to.q},${to.r}`) v -= 26;
  }
  if (a.k === 'place' && a.convert) v += 24;
  return v;
}

function search(
  s: GameState,
  me: Seat,
  depth: number,
  alpha: number,
  beta: number,
  w: Weights,
  width: number,
): number {
  if (s.over || depth === 0) return evaluate(s, me, w);
  const moves = orderMoves(s, s.toMove, width);
  if (moves.length === 0) return evaluate(s, me, w);

  if (s.toMove === me) {
    let best = -Infinity;
    for (const m of moves) {
      best = Math.max(best, search(applyUnchecked(s, m), me, depth - 1, alpha, beta, w, width));
      alpha = Math.max(alpha, best);
      if (alpha >= beta) break;
    }
    return best;
  }
  let best = Infinity;
  for (const m of moves) {
    best = Math.min(best, search(applyUnchecked(s, m), me, depth - 1, alpha, beta, w, width));
    beta = Math.min(beta, best);
    if (alpha >= beta) break;
  }
  return best;
}

function orderMoves(s: GameState, mover: Seat, width: number): Action[] {
  const all = legalActions(s);
  if (all.length <= width) return all;
  return all
    .map((a) => ({ a, v: orderScore(s, a, mover) }))
    .sort((x, y) => y.v - x.v)
    .slice(0, width)
    .map((x) => x.a);
}

export function chooseAction(s: GameState, cfg: AiConfig): Action {
  const w = POLICIES[cfg.policy ?? 'balanced'];
  const depth = DEPTH[cfg.strength];
  const width = WIDTH[cfg.strength];
  const me = s.toMove;
  const rng: Rng = makeRng(`${cfg.seed}:${s.ply}`);

  const moves = orderMoves(s, me, width);
  if (moves.length === 0) return { k: 'pass' };

  let best: Action = moves[0];
  let bestV = -Infinity;
  let alpha = -Infinity;
  for (const m of moves) {
    // The jitter is the tie-break, and it is SEEDED: two bots handed the same
    // position must play the same move, or a 400-game sim measures the coin and
    // not the game. It is small enough never to reorder genuinely unequal moves.
    const v = search(applyUnchecked(s, m), me, depth - 1, alpha, Infinity, w, width) + rng() * 0.5;
    if (v > bestV) {
      bestV = v;
      best = m;
      alpha = Math.max(alpha, v);
    }
  }
  return best;
}
